import _ from "lodash";
import { useCallback, useEffect, useState } from "react";

import { InventoryAPI } from "api/inventory";
import { useRootStore } from "store";
import config from "config/config";
import { Icon } from "components/icon/Icon";
import { captureEvent } from "components/posthog/customEventCapture";
import { ArticleItem, Entry } from "models/inventory/inventory";
import InventoryRow from "../inventoryRow/InventoryRow";

import "./InventoryTable.scss";

type Props = {
  isInternalUser: boolean;
  onGoBack: () => void;
  onInventoryComplete?: (inventoryRows: Entry[]) => void;
  onInventorySave?: (inventoryRows: Entry[]) => void;
};

type Category1 = {
  id: number;
  name: string;
};

type Category2 = {
  id: number;
  category1_id: number;
};

type Category3 = {
  id: number;
  category2_id: number;
};

export default function InventoryTable({
  isInternalUser,
  onGoBack,
  onInventoryComplete,
  onInventorySave
}: Props) {
  const {
    inventory: inventoryState,
    user,
    error
  } = useRootStore((state) => state);
  const { articleRegister, lastYearsInventory, inventory } = inventoryState;
  const userId = user.userInfo ? user.userInfo.id : "";

  const [inventoryRows, setInventoryRows] = useState<Entry[]>([]);
  const [infoTexts, setInfoTexts] = useState<string[]>([]);

  const getItemComment = (item: ArticleItem) => {
    return item.comment === null ? "" : item.comment;
  };

  const lastYearsInventoryMessage = `Raderna nedan är kopierade från senaste slutförda inventering (${
    lastYearsInventory && lastYearsInventory.per_date
  }).`;

  const setDefaultRows = useCallback(async () => {
    if (_.isEmpty(lastYearsInventory)) {
      return;
    }
    console.debug("Setting state from last year");

    const mapEntry = (entry: Entry) => mapEntryToRow(entry);

    const handleResult = (result: any) => {
      try {
        const rows = result.entries
          .map(mapEntry)
          .sort((a: any, b: any) => a.row_id - b.row_id);

        if (!infoTexts.includes(lastYearsInventoryMessage)) {
          setInfoTexts((prevTexts) => [
            ...prevTexts,
            lastYearsInventoryMessage
          ]);
        }
        setInventoryRows(rows);
      } catch (err) {
        console.error("Error parsing result text:", err);
      }
    };

    try {
      const response = await InventoryAPI.getInventory(
        lastYearsInventory?.customer_id ?? "",
        lastYearsInventory?.id ?? 0
      );
      handleResult(response);
    } catch (err) {
      error.appendErrorNotification("Failed to  fetch inventory", err);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastYearsInventory, articleRegister]);

  const mapEntryToRow = useCallback(
    (entry: Entry) => {
      const item = articleRegister?.items.find((i) => i.id === entry.item_id);

      if (!item) {
        throw new Error(`Item with id ${entry.item_id} not found.`);
      }

      const category3Id = item.category3_id;

      const category2Id = articleRegister?.category3s.find(
        (c3) => c3.id === category3Id
      )?.category2_id;

      const category1Id = articleRegister?.category2s.find(
        (c2) => c2.id === category2Id
      )?.category1_id;

      return {
        category1Id: category1Id,
        category2Id: category2Id,
        category3Id: category3Id,
        comment: entry.comment === null ? "" : entry.comment,
        item_id: entry.item_id,
        itemComment: getItemComment(item),
        row_id: entry.row_id,
        unit_id: entry.unit_id,
        user: entry.user,
        quantity: entry.quantity
      };
    },
    [articleRegister]
  );

  const createRow = () => {
    const newRow: any = {
      row_id:
        inventoryRows.length > 0
          ? Math.max(...inventoryRows.map((row) => row.row_id)) + 1
          : 1
    };
    newRow.category1Id = articleRegister?.category1s[0].id;
    newRow.category2Id = articleRegister?.category2s.find(
      (c2) => c2.category1_id === newRow.category1Id
    )?.id;
    newRow.category3Id = articleRegister?.category3s.find(
      (c3) => c3.category2_id === newRow.category2Id
    )?.id;
    newRow.comment = "";
    const item = articleRegister?.items.find(
      (i) => i.category3_id === newRow.category3Id
    );
    if (!item) {
      throw new Error(`Item with id ${newRow.category3Id} not found.`);
    }
    newRow.item_id = item.id;
    newRow.itemComment = getItemComment(item);
    newRow.unit_id = item.unit_id; // start with unit associated with item
    newRow.user = userId;
    newRow.quantity = 1;

    return newRow;
  };

  useEffect(() => {
    const isEmpty = inventory?.entries && inventory.entries.length === 0;
    if (isEmpty && !isInternalUser) {
      setDefaultRows();
    } else {
      if (inventory?.entries) {
        const rows = inventory?.entries
          .map((entry) => mapEntryToRow(entry))
          .sort((a, b) => a.row_id - b.row_id);
        if (rows && inventoryRows.length === 0) {
          setInventoryRows(rows);
        }
      }
    }

    if (isEmpty && isInternalUser) {
      const infoText =
        "Inga poster hittade för denna inventering. Om det finns en slutförd inventering från ett tidigare år kommer kunden se en kopia på denna. Ändringar som görs i kopian påverkar inte tidigare år.";
      setInfoTexts((prevTexts) => [...prevTexts, infoText]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory, mapEntryToRow]);

  const handleAddRow = () => {
    setInventoryRows([...inventoryRows, createRow()]);
  };

  const handleRemoveRow = (value: number) => {
    const rowIdToRemove = value;
    const newRows = inventoryRows.filter((row) => row.row_id !== rowIdToRemove);
    setInventoryRows(newRows);
  };

  const handleCategoryChange = (
    rowId: number,
    value: number,
    categoryLevel: 1 | 2 | 3
  ) => {
    const newRows: Entry[] = _.cloneDeep(inventoryRows);
    const row = newRows.find((newRow) => newRow.row_id === rowId);

    if (!row) {
      throw new Error(`Row with ID ${rowId} not found.`);
    }

    // Declare the variables with specific types
    let category1Id: number | undefined;
    let category2Id: number | undefined;
    let category3Id: number | undefined;
    let category1: Category1 | undefined;
    let category2: Category2 | undefined;
    let category3: Category3 | undefined;
    let item;

    // Handle the category based on the category level
    if (categoryLevel === 1) {
      category1Id = value;
      category1 = articleRegister?.category1s.find(
        (c1) => c1.id === category1Id
      );
      if (category1) {
        category2 = articleRegister?.category2s.find(
          (c2) => c2.category1_id === category1?.id
        );
        if (category2) {
          category3 = articleRegister?.category3s.find(
            (c3) => c3.category2_id === category2?.id
          );
          if (category3) {
            item = articleRegister?.items.find(
              (i) => i.category3_id === category3?.id
            );
          }
        }
      }
      if (category1 && category2 && category3 && item) {
        row.category1Id = category1.id;
        row.category2Id = category2.id;
        row.category3Id = category3.id;
      }
    } else if (categoryLevel === 2) {
      category2Id = value;
      category2 = articleRegister?.category2s.find(
        (c2) => c2.id === category2Id
      );
      if (category2) {
        category3 = articleRegister?.category3s.find(
          (c3) => c3.category2_id === category2?.id
        );
        if (category3) {
          item = articleRegister?.items.find(
            (i) => i.category3_id === category3?.id
          );
        }
      }
      if (category2 && category3 && item) {
        row.category2Id = category2.id;
        row.category3Id = category3.id;
      }
    } else if (categoryLevel === 3) {
      category3Id = value;
      category3 = articleRegister?.category3s.find(
        (c3) => c3.id === category3Id
      );
      if (category3) {
        item = articleRegister?.items.find(
          (i) => i.category3_id === category3?.id
        );
      }
      if (category3 && item) {
        row.category3Id = category3Id;
      }
    }

    if (!item) {
      throw new Error("Item not defined");
    }

    row.item_id = item.id;
    row.itemComment = getItemComment(item);
    row.unit_id = item.unit_id;
    row.user = userId;

    setInventoryRows(newRows);
  };

  const handleCommentChange = (rowId: number, value: string) => {
    const comment = value;
    const newRows = _.cloneDeep(inventoryRows);
    const row = newRows.find((newRow) => newRow.row_id === rowId);
    if (row) {
      row.comment = comment;
      row.user = userId;
    }
    setInventoryRows(newRows);
  };

  const handleInventoryComplete = () => {
    if (onInventoryComplete) {
      onInventoryComplete(inventoryRows);
    }
  };

  const handleInventorySave = () => {
    const newInfoTexts = infoTexts.filter(
      (message) => message !== lastYearsInventoryMessage
    );
    setInfoTexts(newInfoTexts);
    if (onInventorySave) {
      onInventorySave(inventoryRows);
    }
  };

  const handleItemChange = (rowId: number, value: string) => {
    const itemId = parseInt(value);
    const newRows = _.cloneDeep(inventoryRows);
    const row = newRows.find((newRow) => newRow.row_id === rowId);

    const item = articleRegister?.items.find((i) => i.id === itemId);
    if (!item) {
      throw new Error("Inventory item not found");
    }
    if (row) {
      row.item_id = item.id;
      row.itemComment = getItemComment(item);
      row.unit_id = item.unit_id;
      row.user = userId;
    }

    setInventoryRows(newRows);
  };

  const handleQuantityChange = (rowId: number, value: string) => {
    if (value === "") {
      return;
    }

    const quantity = parseInt(value);
    if (quantity < 1) {
      return;
    }

    const newRows = _.cloneDeep(inventoryRows);
    const row = newRows.find((newRow) => newRow.row_id === rowId);
    if (row) {
      row.quantity = quantity;
      row.user = userId;
    }

    setInventoryRows(newRows);
  };

  const handleUnitChange = (rowId: number, value: string) => {
    const unitId = parseInt(value);
    const newRows = _.cloneDeep(inventoryRows);
    const row = newRows.find((newRow) => newRow.row_id === rowId);
    if (row) {
      row.unit_id = unitId;
      row.user = userId;
    }
    setInventoryRows(newRows);
  };

  const isCompleted = inventory?.state_name === "completed";

  return (
    <div className="inventoryTableWrapper">
      <div>
        <button
          className="backButton"
          onClick={() => {
            onGoBack();
            captureEvent("Gå tillbaka");
          }}
        >
          <Icon name="angle-left" /> Gå tillbaka
        </button>
        {!isInternalUser && (
          <a
            rel="noreferrer"
            className="instructionsLink"
            href={config.static.inventoryInstructions}
            target="_blank"
          >
            Instruktion för inventering
          </a>
        )}
      </div>
      {infoTexts.length > 0 && (
        <div className="infoText">
          {infoTexts.map((text, index) => (
            <div key={"infoTextWrapper" + index}>
              <span key={"infoText" + index}>{text}</span>
              {index + 1 < infoTexts.length && <hr></hr>}
            </div>
          ))}
        </div>
      )}
      <div>
        {inventoryRows.map((row) => (
          <InventoryRow
            key={row.row_id}
            category1s={articleRegister?.category1s}
            category2s={articleRegister?.category2s}
            category3s={articleRegister?.category3s}
            id={row.row_id}
            category1Id={row.category1Id ?? 0}
            category2Id={row.category2Id ?? 0}
            category3Id={row.category3Id ?? 0}
            comment={row.comment ?? ""}
            itemId={row.item_id ?? 0}
            itemComment={row.itemComment ?? ""}
            unitId={row.unit_id ?? 0}
            isDisabled={isInternalUser || isCompleted}
            isDeleteDisabled={false}
            isInternalUser={isInternalUser}
            quantity={row.quantity ?? 0}
            onCategoryChange={handleCategoryChange}
            onCommentChange={handleCommentChange}
            onItemChange={handleItemChange}
            onQuantityChange={handleQuantityChange}
            onUnitChange={handleUnitChange}
            onRemoveRow={handleRemoveRow}
          />
        ))}
      </div>
      <div className="inventory-footer">
        {!isInternalUser && (
          <div>
            <button
              className="controlButton"
              disabled={isCompleted}
              onClick={() => {
                handleAddRow();
                captureEvent("Lägg till rad");
              }}
            >
              Lägg till rad
            </button>
            <button
              className="controlButton"
              disabled={isCompleted}
              onClick={() => {
                handleInventorySave();
                captureEvent("Spara");
              }}
            >
              Spara
            </button>
            <button
              className="controlButton"
              disabled={isCompleted}
              onClick={() => {
                handleInventoryComplete();
                captureEvent("Klarmarkera");
              }}
            >
              Klarmarkera
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
